// The Closet — the collection center for your buddy. 👕 wardrobe: owned
// accessories become wearable chips (duplicates collapse to ×N) and a live
// preview wears the outfit. 🐟 aquarium: the pond dex — caught fish in color,
// the rest as silhouettes. <Closet review> (or ?closet=all) still lays out the
// full 22-design approval sheet on a mannequin. Zero art files, as always.
// the fish painters live HERE (garden.js imports them) so the dependency only
// ever flows garden → closet — never back.
import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import htm from 'htm';
import {
  animateSprite, drawAccessory, petSeedFor, hashString, spriteFor,
  ACCESSORIES, ACC_TINT, PALETTES, SPRITE_W, SPRITE_H,
} from './pet.js';

const html = htm.bind(h);

const SLOTS = ['hat', 'shoes', 'necklace'];
const SLOT_GLYPH = { hat: '🎩', shoes: '👟', necklace: '📿' };
const RARITIES = ['common', 'uncommon', 'rare', 'legendary'];
const TOTAL = SLOTS.reduce((a, s) => a + Object.keys(ACCESSORIES[s]).length, 0); // 22
const wordy = (d) => d.replace(/_/g, ' ');

// a buddy wearing an outfit — animated, so legendary pieces actually sparkle
function DressedBuddy({ seed, equip, cell = 7, stage = 'adult', wins = 0, happiness }) {
  const ref = useRef(null);
  const eqKey = JSON.stringify(equip ?? null);
  useEffect(() => {
    if (!ref.current) return;
    return animateSprite(ref.current, seed, { cell, stage, wins, happiness, equip });
  }, [seed, cell, stage, wins, eqKey]);
  return html`<canvas ref=${ref} class="pixel" width=${SPRITE_W * cell} height=${SPRITE_H * cell}
    style="width:${SPRITE_W * cell}px;height:${SPRITE_H * cell}px" />`;
}

// the accessory alone (chips, headers); shoes render as the pair. slow two-frame
// tick keeps the shimmering pieces shimmering without burning the battery
function AccIcon({ slot, design, cell = 3 }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    let frame = 0;
    let alive = true;
    const tick = () => {
      if (!alive || !cv.isConnected) return;
      drawAccessory(cv, slot, design, { cell, frame: frame++ });
      setTimeout(tick, 600);
    };
    tick();
    return () => {
      alive = false;
    };
  }, [slot, design, cell]);
  return html`<canvas ref=${ref} class="pixel" width=${11 * cell} height=${5 * cell}
    style="width:${11 * cell}px;height:${5 * cell}px" />`;
}

// ---- pond fish: one hand-set body, palettes by rarity (kind seeds the tint) ----

const FISH_GRID = [
  '..11111...',
  '.1211111.2',
  '1111111122',
  '.1111111.2',
  '..11111...',
];
const FISH_PAL = {
  common: ['#9db8a6', '#6b8776'],
  uncommon: null, // seeded from the kind — every uncommon species has its own coat
  rare: ['#f5c542', '#c78f1e'],
  legendary: ['#b18aff', '#f5c542'],
};

export function FishCanvas({ kind, rarity, cell = 4, shadow = false }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    // shadow = an uncaught dex entry: the same body, all silhouette, no eye or sparkle
    const pal = shadow
      ? ['#2c2940', '#232131']
      : FISH_PAL[rarity] ?? PALETTES[hashString(`vb:fish:${kind}`) % PALETTES.length];
    for (let y = 0; y < 5; y++)
      for (let x = 0; x < 10; x++) {
        const ch = FISH_GRID[y][x];
        if (ch === '.') continue;
        ctx.fillStyle = ch === '2' ? pal[1] : pal[0];
        ctx.fillRect(x * cell, y * cell, cell, cell);
      }
    if (shadow) return;
    ctx.fillStyle = '#16131f';
    ctx.fillRect(2 * cell, 1 * cell, cell, cell); // the eye
    if (rarity === 'legendary') {
      ctx.fillStyle = '#fff3c4';
      ctx.fillRect(0, 0, cell / 2, cell / 2);
      ctx.fillRect(9.5 * cell, 4.5 * cell, cell / 2, cell / 2);
    }
  }, [kind, rarity, cell, shadow]);
  return html`<canvas ref=${ref} class="pixel" width=${10 * cell} height=${5 * cell}
    style="width:${10 * cell}px;height:${5 * cell}px;vertical-align:middle" />`;
}

// the aquarium dex — mirrors FISH_POOL in server/src/api.js (the wire-format registry)
const FISH_DEX = [
  ['minnow', 'common'], ['bitterling', 'common'], ['mossback', 'common'],
  ['koi', 'uncommon'], ['catfish', 'uncommon'], ['glassfin', 'uncommon'],
  ['golden koi', 'rare'], ['moonfish', 'rare'],
  ['pixel leviathan', 'legendary'],
];

// the review sheet fits everything on ONE body: hunt a plain common blob so the
// clothes — not the model — are what the eye compares
let mannCache = null;
function mannequinSeed() {
  if (mannCache != null) return mannCache;
  for (let i = 0; i < 400; i++) {
    const s = hashString(`vb:mannequin:${i}`);
    const sp = spriteFor(s);
    // a plain common blob with no pattern pass — the least distracting body
    if (sp.species === 'blob' && sp.rarity === 'common' && !sp.grid.some((row) => row.includes(8))) {
      mannCache = s;
      return s;
    }
  }
  mannCache = hashString('vb:mannequin:0');
  return mannCache;
}

// ---- dev review mode: all 22 designs, slot × rarity, at big AND list size ----

function ReviewSheet({ onClose }) {
  const seed = mannequinSeed();
  return html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && onClose?.()}>
    <div class="zoomcard" style="max-width:880px;width:min(96vw,880px);max-height:92vh;overflow-y:auto">
      <div class="gardenhead">
        <b>👕 the closet — all ${TOTAL} designs</b>
        ${onClose && html`<span class="closex clickable" onClick=${onClose}>✕</span>`}
      </div>
      <div class="dim" style="margin:-4px 0 8px">
        approval sheet · one mannequin, every piece · big fit (cell 6) beside list fit (cell 3)
      </div>
      ${SLOTS.map(
        (slot) => html`<div style="margin:6px 0 16px">
          <div class="sectionhead" style="font-size:15px;border-bottom:1px solid var(--border);padding-bottom:4px">
            ${SLOT_GLYPH[slot]} ${slot}
          </div>
          ${RARITIES.map((r) => {
            const designs = Object.keys(ACCESSORIES[slot]).filter((d) => ACCESSORIES[slot][d].rarity === r);
            if (!designs.length) return null;
            return html`<div style="margin:8px 0 12px">
              <div style="font-size:11px;letter-spacing:1.5px;color:${ACC_TINT[r]};margin-bottom:6px">
                ${r.toUpperCase()}
              </div>
              <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:10px">
                ${designs.map(
                  (d) => html`<div style="border:1px solid ${ACC_TINT[r]};background:var(--surface-2);padding:8px 6px;display:flex;flex-direction:column;align-items:center;gap:6px">
                    <div style="display:flex;align-items:flex-end;gap:10px">
                      <${DressedBuddy} seed=${seed} equip=${{ [slot]: d }} cell=${6} />
                      <${DressedBuddy} seed=${seed} equip=${{ [slot]: d }} cell=${3} />
                    </div>
                    <${AccIcon} slot=${slot} design=${d} cell=${3} />
                    <div style="font-size:12px;text-align:center">${wordy(d)}</div>
                  </div>`
                )}
              </div>
            </div>`;
          })}
        </div>`
      )}
    </div>
  </div>`;
}

// ---- the collection center: 👕 wardrobe + 🐟 aquarium under one roof ----

export function Closet({ me, onToast, onClose, review = false, tab = 'wardrobe' }) {
  const isReview =
    review || (typeof location !== 'undefined' && new URLSearchParams(location.search).get('closet') === 'all');
  const [cur, setCur] = useState(tab === 'aquarium' ? 'aquarium' : 'wardrobe');
  const [inv, setInv] = useState(null); // { items, equip, collected, total } | 'error'
  const [meta, setMeta] = useState(null);
  const [fishLog, setFishLog] = useState(null); // [{ kind, rarity, n }] | 'error'
  const [busy, setBusy] = useState(false);
  const seed = me ? petSeedFor(me.username) : 0;

  useEffect(() => {
    if (isReview || !me) return;
    // the wardrobe endpoint may still be moving in — 404s degrade to a gentle note
    fetch('/api/me/accessories')
      .then(async (r) => setInv(r.ok ? await r.json() : 'error'))
      .catch(() => setInv('error'));
    fetch(`/api/pet/${me.username}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setMeta)
      .catch(() => {});
  }, [isReview, me?.username]);

  // the aquarium reads the same payload the pond log does — fetched on first visit
  useEffect(() => {
    if (isReview || !me || cur !== 'aquarium' || fishLog !== null) return;
    fetch(`/api/garden/${me.username}`)
      .then(async (r) => setFishLog(r.ok ? ((await r.json()).fish_log ?? []) : 'error'))
      .catch(() => setFishLog('error'));
  }, [isReview, me?.username, cur]);

  if (isReview) return html`<${ReviewSheet} onClose=${onClose} />`;

  const logOk = Array.isArray(fishLog);
  const byKind = new Map(logOk ? fishLog.map((f) => [f.kind, f]) : []);
  const caught = FISH_DEX.filter(([k]) => byKind.has(k)).length;

  const ok = inv && inv !== 'error';
  const equip = ok ? (inv.equip ?? {}) : {};
  const items = ok ? (inv.items ?? []) : [];
  // duplicates collapse to one chip with a ×N badge
  const owned = {};
  for (const it of items) {
    const k = `${it.slot}:${it.design}`;
    owned[k] = owned[k] ? { ...owned[k], n: owned[k].n + 1 } : { ...it, n: 1 };
  }
  const bySlot = (slot) =>
    Object.values(owned)
      .filter((o) => o.slot === slot)
      .sort((a, b) => RARITIES.indexOf(a.rarity) - RARITIES.indexOf(b.rarity) || a.design.localeCompare(b.design));
  const collected = ok ? (inv.collected ?? Object.keys(owned).length) : 0;

  const toggle = async (slot, design) => {
    if (busy) return;
    setBusy(true);
    try {
      const next = { ...equip };
      if (next[slot] === design) delete next[slot];
      else next[slot] = design;
      const r = await fetch('/api/prefs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ equip: next }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return onToast?.(d.error ?? 'the drawer jammed — try again');
      const worn = d.prefs?.equip ?? next;
      setInv((prev) => (prev && prev !== 'error' ? { ...prev, equip: worn } : prev));
      onToast?.(worn[slot] === design ? `${wordy(design)} on ✨` : `${wordy(design)} off`);
    } finally {
      setBusy(false);
    }
  };

  return html`<div class="overlay" onClick=${(e) => e.target.classList.contains('overlay') && onClose?.()}>
    <div class="zoomcard" style="max-width:640px;width:min(96vw,640px);max-height:90vh;overflow-y:auto">
      <div class="gardenhead">
        <b>${cur === 'wardrobe' ? '👕 the closet' : `🐟 the aquarium · ${logOk ? caught : '…'}/${FISH_DEX.length}`}</b>
        <span class="closex clickable" onClick=${onClose}>✕</span>
      </div>
      <div class="pillrow" style="margin-bottom:12px">
        <button class="mini ${cur === 'wardrobe' ? 'primary' : ''}" onClick=${() => setCur('wardrobe')}>👕 wardrobe</button>
        <button class="mini ${cur === 'aquarium' ? 'primary' : ''}" onClick=${() => setCur('aquarium')}>🐟 aquarium</button>
      </div>
      ${cur === 'wardrobe' &&
      html`<div style="display:flex;gap:16px;align-items:flex-start;flex-wrap:wrap">
        <div style="display:flex;flex-direction:column;align-items:center;gap:6px;padding:12px 16px;background:var(--surface-2);border:1px solid var(--border)">
          <${DressedBuddy} seed=${seed} equip=${equip} cell=${8}
            stage=${meta?.stage ?? 'adult'} wins=${meta?.wins ?? 0} happiness=${meta?.happiness} />
          <div class="dim" style="font-size:12px">${me?.pet_name ?? me?.username}'s fit</div>
        </div>
        <div style="flex:1;min-width:260px">
          ${SLOTS.map(
            (slot) => html`<div style="margin-bottom:14px">
              <div class="sectionhead">
                ${SLOT_GLYPH[slot]} ${slot}
                ${equip[slot] && html`<span class="dim" style="font-weight:400"> · wearing ${wordy(equip[slot])}</span>`}
              </div>
              ${bySlot(slot).length
                ? html`<div class="pillrow">
                    ${bySlot(slot).map((o) => {
                      const worn = equip[slot] === o.design;
                      const tint = ACC_TINT[o.rarity] ?? ACC_TINT.common;
                      return html`<span class="clickable" title="${o.rarity} · ${worn ? 'tap to take off' : 'tap to wear'}"
                        onClick=${() => toggle(o.slot, o.design)}
                        style="display:inline-flex;flex-direction:column;align-items:center;gap:2px;padding:6px 8px;border:1px solid ${tint};background:${worn ? `color-mix(in srgb, ${tint} 20%, var(--surface-2))` : 'var(--surface-2)'}">
                        <${AccIcon} slot=${o.slot} design=${o.design} cell=${3} />
                        <span style="font-size:11px">${wordy(o.design)}${o.n > 1 ? ` ×${o.n}` : ''}</span>
                        ${worn && html`<span style="font-size:10px;color:var(--mint-d)">worn ✓</span>`}
                      </span>`;
                    })}
                  </div>`
                : html`<div class="dim" style="font-size:12px">
                    ${ok || inv === 'error' ? 'nothing here yet — the big tree provides 🍑' : '…'}
                  </div>`}
            </div>`
          )}
          ${inv === 'error' &&
          html`<div class="dim" style="font-size:12px">
            the wardrobe is still being carried in — harvested fruit will appear here soon.
          </div>`}
        </div>
      </div>
      <div class="gardenhint" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px">
        <span>collected <b>${collected}</b>/${ok ? (inv.total ?? TOTAL) : TOTAL}</span>
        ${RARITIES.map(
          (r) => html`<span style="display:inline-flex;align-items:center;gap:4px;font-size:11px;color:${ACC_TINT[r]}">
            <span style="width:7px;height:7px;background:${ACC_TINT[r]};display:inline-block"></span>${r}
          </span>`
        )}
      </div>`}
      ${cur === 'aquarium' &&
      html`${fishLog === null && html`<div class="dim">…</div>`}
      ${fishLog === 'error' &&
      html`<div class="dim" style="font-size:12px">
        the pond ledger is out of reach — cast a line from your garden and try again.
      </div>`}
      ${logOk &&
      html`<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(128px,1fr));gap:10px">
        ${FISH_DEX.map(([kind, rarity]) => {
          const hit = byKind.get(kind);
          const tint = ACC_TINT[rarity] ?? ACC_TINT.common;
          return html`<div style="border:1px solid ${hit ? tint : 'var(--border)'};background:var(--surface-2);padding:10px 6px;display:flex;flex-direction:column;align-items:center;gap:6px">
            <${FishCanvas} kind=${kind} rarity=${rarity} cell=${4} shadow=${!hit} />
            <div style="font-size:12px;text-align:center">${hit ? kind : '???'}</div>
            <div style="font-size:11px;color:${tint}">${hit ? `${rarity} ×${hit.n}` : `a ${rarity} still swims out there`}</div>
          </div>`;
        })}
      </div>
      <div class="gardenhint" style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px">
        <span>hooked <b>${caught}</b>/${FISH_DEX.length}</span>
        ${RARITIES.map(
          (r) => html`<span style="display:inline-flex;align-items:center;gap:4px;font-size:11px;color:${ACC_TINT[r]}">
            <span style="width:7px;height:7px;background:${ACC_TINT[r]};display:inline-block"></span>${r}
          </span>`
        )}
        <span class="dim" style="font-size:11px">tap open water in your garden to cast 🎣</span>
      </div>`}`}
    </div>
  </div>`;
}
